import {
  Button,
  Card,
  CardBody,
  CloseButton,
  Collapse,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Select,
  Text,
  useDisclosure,
  useToken,
  VisuallyHidden,
} from '@chakra-ui/react';
import { ErrorMessage } from '@hookform/error-message';
import { PriceStrategy, priceStrategySchema } from '@snap-types/models/enums';
import type { PriceStrategyConfig } from '@snap-types/models/price-strategy-config';
import type { SnapSiteConfigEditFormDataUnvalidated } from '@snap-types/models/snap-site-config';
import type { FieldPathFn } from '@ui/components/form';
import { CircleInfoIcon, PlusIcon } from '@ui/components/Icons';
import { priceStrategyUIConfig } from '@ui/config/price/strategies';
import { EnabledArrayActions } from '@ui/features/snap-site-config/screens/edit/components/ArrayActions';
import { EnabledForm } from '@ui/features/snap-site-config/screens/edit/components/EnabledForm';
import { FormRowActions } from '@ui/features/snap-site-config/screens/edit/components/FormRowActions';
import { LeadTimeConfigForm } from '@ui/features/snap-site-config/screens/edit/components/LeadTimeConfigForm/LeadTimeConfigForm';
import { NewPartUserCostForm } from '@ui/features/snap-site-config/screens/edit/components/PriceStrategyConfigsForm/price-strategy-config-types/NewPartUserCostForm';
import { ShowWhenEnabled } from '@ui/features/snap-site-config/screens/edit/components/ShowWhenEnabled';
import { useSnapSiteConfigEditContext } from '@ui/features/snap-site-config/screens/edit/context/hook';
import { useCurrentSite } from '@ui/hooks/useCurrentSite';
import {
  type FieldPath,
  useFieldArray,
  type UseFieldArrayReturn,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { match } from 'ts-pattern';

import {
  getAvailablePriceStrategies,
  getDefaultPriceStrategyParamsForType,
  priceStrategyConfigOptionLabels,
} from './data';
import { CAPPriceSegmentationForm } from './price-strategy-config-types/CAPPriceSegmentation/CAPPriceSegmentationForm';
import { FixedCustomerForm } from './price-strategy-config-types/FixedCustomerForm';
import { FixedDefaultForm } from './price-strategy-config-types/FixedDefaultForm';
import { FixedDiscountForm } from './price-strategy-config-types/FixedDiscountForm';
import { FixedMarketForm } from './price-strategy-config-types/FixedMarketForm';
import { NewPartForm } from './price-strategy-config-types/NewPartForm';
import { NewPartUserPriceForm } from './price-strategy-config-types/NewPartUserPriceForm';
import { PriceSegmentationForm } from './price-strategy-config-types/PriceSegmentation/PriceSegmentationForm';
import { RAFFixedMarketForm } from './price-strategy-config-types/RAFFixedMarketForm';
import { RAFTargetGrossMarginForm } from './price-strategy-config-types/RAFTargetGrossMarginForm';
import { TargetContributionMarginForm } from './price-strategy-config-types/TargetContributionMarginForm';
import { TargetGrossMarginForm } from './price-strategy-config-types/TargetGrossMarginForm';
import { UserImportForm } from './price-strategy-config-types/UserImportForm';
import { UserPriceForm } from './price-strategy-config-types/UserPriceForm';

export function PriceStrategyConfigsForm() {
  const form = useFormContext<SnapSiteConfigEditFormDataUnvalidated>();
  const { discountCodes, marketCodes } = useSnapSiteConfigEditContext();

  const priceStrategies = useFieldArray({
    control: form.control,
    name: 'priceStrategies',
  });

  return (
    <Flex px={4} direction="column" gap={4}>
      {priceStrategies.fields.length === 0 ? <EmptyState /> : null}

      {priceStrategies.fields.map((item, index) => {
        const priceStrategyFieldPath: FieldPathFn<
          SnapSiteConfigEditFormDataUnvalidated
        > = (fieldName) =>
          `priceStrategies.${index}.${fieldName}` as FieldPath<SnapSiteConfigEditFormDataUnvalidated>;

        return (
          <FormRow
            id={item.id}
            key={item.id}
            fieldActions={priceStrategies}
            index={index}
            fieldPath={priceStrategyFieldPath}
          />
        );
      })}

      <FormRowActions>
        <AddPriceStrategyConfigButton
          onClick={(type: PriceStrategy) => {
            priceStrategies.append(
              getDefaultPriceStrategyParamsForType(type, {
                marketCodes,
                discountCodes,
              }),
            );
          }}
        />
      </FormRowActions>
    </Flex>
  );
}

function EmptyState() {
  const { isOpen, onClose, onToggle } = useDisclosure();

  return (
    <Flex direction="column">
      <Flex
        align="center"
        gap={2}
        p={4}
        rounded="sm"
        bg="snap.teal.50"
        flexWrap="wrap"
      >
        <CircleInfoIcon color="snap.teal.400" />
        <Text fontWeight="bold" fontSize="14px" color="snap.teal.500">
          No Price Strategy Configs set up.
        </Text>

        <Text fontSize="14px">Add a config to get started.</Text>

        <Button
          variant="outline"
          size="xs"
          colorScheme="teal"
          onClick={onToggle}
        >
          What is a Price Strategy Config?
        </Button>
      </Flex>

      <Collapse in={isOpen} animateOpacity>
        <Flex p={4} bg="mw.panelGrey" mt={4} gap={2} rounded="sm">
          <Text>
            Price Strategy Configs allow you to define how prices are calculated
            for different customers or scenarios. You can set up multiple
            configs to handle various pricing strategies.
          </Text>

          <CloseButton onClick={onClose} />
        </Flex>
      </Collapse>
    </Flex>
  );
}

function FormRow({
  id,
  fieldPath,
  fieldActions,
  index,
}: {
  id: string;
  fieldPath: FieldPathFn<SnapSiteConfigEditFormDataUnvalidated>;
  fieldActions: UseFieldArrayReturn<SnapSiteConfigEditFormDataUnvalidated>;
  index: number;
}) {
  const form = useFormContext<SnapSiteConfigEditFormDataUnvalidated>();
  const currentSite = useCurrentSite();

  const enabledFieldPath = fieldPath('enabled');
  const priceStrategyConfigTypeFieldPath = fieldPath('priceStrategyType');

  const priceStrategyConfigType = useWatch({
    name: priceStrategyConfigTypeFieldPath,
    control: form.control,
  }) as PriceStrategy;

  const priceStrategyConfigs = useWatch({
    name: 'priceStrategies',
    control: form.control,
  });

  const availablePriceStrategies = getAvailablePriceStrategies(
    currentSite.code,
    (priceStrategyConfigs ?? [])
      .map((config) => config.priceStrategyType)
      .filter((type) => type !== priceStrategyConfigType),
  );

  const uiConfig = priceStrategyUIConfig[priceStrategyConfigType];
  const priceStrategyColorHex = useToken('colors', [
    `${uiConfig.badgeColor}.500`,
  ]);

  return (
    <Card
      variant="outline"
      role="group"
      data-group-scope="price-strategy-config"
      sx={{
        viewTransitionName: `price-strategy-config-${id}`,
        animationDuration: '0.2s',
        animationTimingFunction: 'cubic-bezier(0.1, 1, 0.3, 1)',
      }}
      borderLeftColor={priceStrategyColorHex}
      borderLeftWidth={6}
    >
      <CardBody gap={4} display="flex">
        <Flex flex="1" gap={4} direction="column" justifyContent="center">
          <EnabledForm
            enabledPath={enabledFieldPath}
            otherFieldPath={fieldPath('priceStrategyType')}
            formatValue={(value) => {
              if (value) {
                return priceStrategyConfigOptionLabels[
                  priceStrategySchema.parse(value)
                ];
              }

              return String(value);
            }}
          />

          <ShowWhenEnabled enabledPath={enabledFieldPath}>
            <Flex flex="1" gap={4} direction="column">
              <FormControl mb={2}>
                <VisuallyHidden>
                  <FormLabel htmlFor={priceStrategyConfigTypeFieldPath}>
                    Price Strategy:
                  </FormLabel>
                </VisuallyHidden>

                <Select
                  {...form.register(priceStrategyConfigTypeFieldPath)}
                  id={priceStrategyConfigTypeFieldPath}
                >
                  {Object.entries(priceStrategyConfigOptionLabels)
                    .filter(([key]) =>
                      availablePriceStrategies.all.includes(
                        key as PriceStrategy,
                      ),
                    )
                    .map(([value, label]) => (
                      <option
                        key={value}
                        value={value}
                        disabled={
                          !availablePriceStrategies.available.includes(
                            value as PriceStrategy,
                          )
                        }
                      >
                        {label}
                      </option>
                    ))}
                </Select>

                <ErrorMessage
                  name={priceStrategyConfigTypeFieldPath}
                  render={({ message }) => (
                    <FormErrorMessage>{message}</FormErrorMessage>
                  )}
                />
              </FormControl>

              {match(priceStrategyConfigType)
                .returnType<React.ReactNode>()
                .with(PriceStrategy.TargetGrossMargin, () => (
                  <TargetGrossMarginForm fieldPath={fieldPath} />
                ))
                .with(PriceStrategy.TargetContributionMargin, () => (
                  <TargetContributionMarginForm fieldPath={fieldPath} />
                ))
                .with(PriceStrategy.PriceSegmentation, () => (
                  <PriceSegmentationForm fieldPath={fieldPath} />
                ))
                .with(PriceStrategy.CAPPriceSegmentation, () => (
                  <CAPPriceSegmentationForm fieldPath={fieldPath} />
                ))
                .with(PriceStrategy.FixedCustomer, () => <FixedCustomerForm />)
                .with(PriceStrategy.FixedDefault, () => <FixedDefaultForm />)
                .with(PriceStrategy.FixedDiscount, () => (
                  <FixedDiscountForm fieldPath={fieldPath} />
                ))
                .with(PriceStrategy.FixedMarket, () => (
                  <FixedMarketForm fieldPath={fieldPath} />
                ))
                .with(PriceStrategy.NewPart, () => (
                  <NewPartForm fieldPath={fieldPath} />
                ))
                .with(PriceStrategy.NewPartUserPrice, () => (
                  <NewPartUserPriceForm />
                ))
                .with(PriceStrategy.NewPartUserCost, () => (
                  <NewPartUserCostForm fieldPath={fieldPath} />
                ))
                .with(PriceStrategy.RAFFixedMarket, () => (
                  <RAFFixedMarketForm fieldPath={fieldPath} />
                ))
                .with(PriceStrategy.RAFTargetGrossMargin, () => (
                  <RAFTargetGrossMarginForm fieldPath={fieldPath} />
                ))
                .with(PriceStrategy.UserImport, () => <UserImportForm />)
                .with(PriceStrategy.UserPrice, () => <UserPriceForm />)
                .exhaustive()}

              <LeadTimeConfigForm
                fieldPath={fieldPath}
                priceStrategy={priceStrategyConfigType}
              />
            </Flex>
          </ShowWhenEnabled>
        </Flex>

        <EnabledArrayActions
          index={index}
          fieldActions={fieldActions}
          enabledPath={enabledFieldPath}
        />
      </CardBody>
    </Card>
  );
}

function AddPriceStrategyConfigButton({
  onClick,
}: {
  onClick: (type: PriceStrategy) => void;
}) {
  const currentSite = useCurrentSite();
  const form = useFormContext();

  const priceStrategyConfigTypes = useWatch({
    name: 'priceStrategies',
    control: form.control,
  });

  const availableStrategies = getAvailablePriceStrategies(
    currentSite.id,
    priceStrategyConfigTypes.map(
      (config: PriceStrategyConfig) => config.priceStrategyType,
    ),
  ).available;

  function handleClick() {
    onClick(availableStrategies[0]);
  }

  return (
    <Button
      size="sm"
      variant="outline"
      onClick={handleClick}
      isDisabled={!availableStrategies.length}
      leftIcon={<PlusIcon />}
    >
      Add Price Strategy Config
    </Button>
  );
}
